import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, SectionTitle } from '../../shared/components';
import { StateChip } from '../components/Chips';
import ReassignDialog from '../components/ReassignDialog';
import TextPromptDialog from '../components/TextPromptDialog';
import { useTaskMeta } from '../components/useTaskMeta';
import { reassignmentsApi } from '../tasks.api';
import type { Reassignment } from '../tasks.types';
import { errorMessage, fmtDate, fmtDateTime, fmtHours, humanize } from '../tasks.utils';

const STATUSES = ['REQUESTED', 'APPROVED', 'ACCEPTED', 'REASSIGNED', 'RELEASED', 'REJECTED', 'DECLINED', 'CANCELLED'];

function ReassignmentTable({ rows, actions }: { rows: Reassignment[]; actions?: (r: Reassignment) => React.ReactNode }) {
  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Task</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>From → To</TableCell>
          <TableCell align='right'>Alloc / actual / left</TableCell>
          <TableCell align='right'>Transferred</TableCell>
          <TableCell>Reason</TableCell>
          <TableCell>Requested</TableCell>
          <TableCell>Status</TableCell>
          {actions && <TableCell />}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id}>
            <TableCell>
              <Link component={RouterLink} to={`/tasks/${r.taskId}`}>{r.taskCode}</Link>
              <Typography variant='caption' display='block'>{r.taskTitle}</Typography>
            </TableCell>
            <TableCell>{humanize(r.requestType)}</TableCell>
            <TableCell>{r.fromEmployee?.name ?? '—'} → {r.toEmployee?.name ?? '—'}</TableCell>
            <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
              {fmtHours(r.previousAllocatedHours)} / {fmtHours(r.previousActualHours)} / {fmtHours(r.previousRemainingHours)}
            </TableCell>
            <TableCell align='right'>{fmtHours(r.transferredHours)}</TableCell>
            <TableCell>{r.reason}</TableCell>
            <TableCell>
              {fmtDateTime(r.requestedAt)}
              <Typography variant='caption' display='block' color='text.secondary'>{r.requestedBy?.name}</Typography>
            </TableCell>
            <TableCell>
              <StateChip status={r.status} label={r.statusLabel} />
              {r.expired && <Typography variant='caption' color='error' display='block'>expired</Typography>}
            </TableCell>
            {actions && <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>{actions(r)}</TableCell>}
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow><TableCell {...({ colSpan: 9 } as object)} align='center' sx={{ py: 3, color: 'text.secondary' }}>Nothing here.</TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  );
}

/** Managers: the release/reassignment queue. Everyone: offers to answer and own requests. */
export default function ReassignmentsPage() {
  const meta = useTaskMeta();
  const manager = Boolean(meta?.permissions.TASK_REASSIGN_APPROVE);
  const [status, setStatus] = useState('REQUESTED');
  const [all, setAll] = useState<Reassignment[]>([]);
  const [mine, setMine] = useState<{ offers: Reassignment[]; requests: Reassignment[] }>({ offers: [], requests: [] });
  const [approving, setApproving] = useState<Reassignment | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; label: string; required?: boolean; run: (t: string) => Promise<unknown> } | null>(null);

  const load = useCallback(async () => {
    try {
      if (meta?.myEmployeeId) setMine(await reassignmentsApi.mine());
      if (manager) setAll((await reassignmentsApi.list(status || undefined)).content);
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load reassignments'));
    }
  }, [manager, status, meta]);

  useEffect(() => {
    if (meta) load();
  }, [meta, load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Stack gap={3}>
      <SectionTitle title='REASSIGNMENTS' />

      {mine.offers.length > 0 && (
        <Card title='WAITING FOR YOUR ANSWER' sx={{ py: 2 }}>
          {mine.offers.map((o) => (
            <Alert key={o.id} severity={o.expired ? 'error' : 'info'}
              action={
                <Stack direction='row' gap={1}>
                  <Button color='inherit' disabled={o.expired} onClick={() => act(() => reassignmentsApi.accept(o.id), 'Task accepted')}>Accept</Button>
                  <Button color='inherit' onClick={() => setPrompt({ title: 'DECLINE TASK', label: 'Why are you declining?', run: (t) => reassignmentsApi.decline(o.id, t) })}>Decline</Button>
                </Stack>
              }>
              {o.taskCode} · {o.taskTitle} from {o.fromEmployee?.name}: {fmtHours(o.transferredHours)}
              {o.expired ? ' — this offer expired.' : `, answer by ${fmtDate(o.expiresAt)}.`}
              {o.remarks && ` ${o.remarks}`}
            </Alert>
          ))}
        </Card>
      )}

      {manager && (
        <Card sx={{ py: 2 }}>
          <Stack direction='row' alignItems='center' gap={2}>
            <Typography variant='subtitle1'>ALL REQUESTS</Typography>
            <TextField select size='small' label='Status' sx={{ minWidth: 160 }} value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value=''>All</MenuItem>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{humanize(s)}</MenuItem>)}
            </TextField>
          </Stack>
          <ReassignmentTable
            rows={all}
            actions={(r) =>
              r.status === 'REQUESTED' ? (
                <>
                  <Button size='small' variant='contained' onClick={() => setApproving(r)}>Review</Button>
                  <Button size='small' color='error' onClick={() => setPrompt({ title: 'REJECT REQUEST', label: 'Remarks', run: (t) => reassignmentsApi.reject(r.id, t) })}>Reject</Button>
                </>
              ) : r.status === 'APPROVED' ? (
                <Button size='small' onClick={() => setPrompt({ title: 'WITHDRAW OFFER', label: 'Remarks', run: (t) => reassignmentsApi.cancel(r.id, t) })}>Withdraw</Button>
              ) : null
            }
          />
        </Card>
      )}

      {meta?.myEmployeeId && (
        <Card title='MY RELEASE REQUESTS' sx={{ py: 2 }}>
          <ReassignmentTable
            rows={mine.requests}
            actions={(r) =>
              r.status === 'REQUESTED' && r.requestedBy?.id !== undefined ? (
                <Button size='small' onClick={() => act(() => reassignmentsApi.cancel(r.id), 'Request withdrawn')}>Withdraw</Button>
              ) : null
            }
          />
        </Card>
      )}

      {approving && meta && (
        <ReassignDialog
          open
          meta={meta}
          request={approving}
          canSkipAcceptance={Boolean(meta.permissions.TASK_REASSIGN_DIRECT)}
          context={{
            taskId: approving.taskId,
            taskCode: approving.taskCode,
            taskTitle: approving.taskTitle,
            currentEmployeeId: approving.fromEmployee?.id ?? null,
            currentEmployeeName: approving.fromEmployee?.name ?? null,
            allocated: approving.previousAllocatedHours ?? 0,
            actual: approving.previousActualHours ?? 0,
            remaining: approving.previousRemainingHours ?? 0,
          }}
          onClose={() => setApproving(null)}
          onDone={() => { setApproving(null); load(); }}
        />
      )}
      {prompt && (
        <TextPromptDialog open title={prompt.title} label={prompt.label} required={prompt.required} onClose={() => setPrompt(null)}
          onSubmit={async (t) => { await act(() => prompt.run(t), 'Done'); setPrompt(null); }} />
      )}
    </Stack>
  );
}
